import React from 'react';
import { makeStyles, Typography, Tooltip, Chip, Icon, Link, Box } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  boothChip: {
    backgroundColor: theme.palette.type === 'dark' ? '#2c3e50' : '#f0f6ff',
    color: theme.palette.type === 'dark' ? '#ecf0f1' : '#2c5282',
    fontWeight: 500,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    border: `1px solid #2c5282`,
    borderRadius: 6,
    padding: 0,
    height: 28,
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#34495e' : '#e6efff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)',
    },
    '& .MuiChip-label': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  chipSmall: {
    height: 22,
  },
  chipLarge: {
    height: 34,
  },
  chipContent: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px 0 0',
    height: '100%',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    width: 28,
    height: 28,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
    marginRight: 8,
  },
  iconWrapperSmall: {
    width: 22,
    height: 22,
    marginRight: 6,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
  },
  iconWrapperLarge: {
    width: 34,
    height: 34,
    marginRight: 10,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
  },
  boothIcon: {
    fontSize: 16,
  },
  boothIconSmall: {
    fontSize: 13,
  },
  boothIconLarge: {
    fontSize: 20,
  },
  label: {
    fontSize: 13,
    letterSpacing: 0.2,
    fontWeight: 500,
  },
  labelSmall: {
    fontSize: 11,
  },
  labelLarge: {
    fontSize: 15,
  },
  boothNumber: {
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: 0.2,
    marginLeft: 3,
  },
  boothNumberSmall: {
    fontSize: 11,
    fontWeight: 600,
  },
  boothNumberLarge: {
    fontSize: 15,
    fontWeight: 600,
  },
  plainText: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
    color: theme.palette.type === 'dark' ? '#ecf0f1' : '#2c5282',
  },
  plainTextSmall: {
    fontSize: 11,
  },
  plainTextLarge: {
    fontSize: 15,
  },
  plainTextIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    width: 20,
    height: 20,
    borderRadius: 4,
    marginRight: 6,
  },
  plainTextIconWrapperSmall: {
    width: 16,
    height: 16,
    marginRight: 4,
  },
  plainTextIconWrapperLarge: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  plainTextIcon: {
    fontSize: 14,
  },
  plainTextIconSmall: {
    fontSize: 12,
  },
  plainTextIconLarge: {
    fontSize: 18,
  },
  plainTextNumber: {
    fontWeight: 600,
    marginLeft: 3,
  },
  plainTextNumberSmall: {
    marginLeft: 0,
  },
}));

/**
 * Format booth ID by adding a dash before the 4th digit if longer than 3 digits
 * @param {string|number} id The booth ID to format
 * @returns {string} The formatted booth ID
 */
const formatBoothId = id => {
  const idStr = String(id);

  if (idStr.length > 3) {
    return `${idStr.substring(0, 3)}-${idStr.substring(3)}`;
  }

  return idStr;
};

/**
 * Reusable component for displaying booth IDs consistently across the application
 *
 * @param {Object} props Component props
 * @param {string} props.boothId The booth ID to display
 * @param {boolean} [props.chip=true] Whether to display as a chip (default) or plain text
 * @param {boolean} [props.linkToDetail=true] Whether to link to the booth detail page
 * @param {string} [props.tooltipText="View Booth Details"] Custom tooltip text
 * @param {string} [props.size="default"] Size of the component: "small", "default", or "large"
 */
export default function BoothIdLink({
  boothId,
  chip = true,
  linkToDetail = true,
  tooltipText = 'View Booth Details',
  size = 'default',
  ...otherProps
}) {
  const classes = useStyles();

  if (!boothId) return null;

  const formattedBoothId = formatBoothId(boothId);

  // Determine classes based on size
  const getChipClass = () => {
    if (size === 'small') return `${classes.boothChip} ${classes.chipSmall}`;
    if (size === 'large') return `${classes.boothChip} ${classes.chipLarge}`;
    return classes.boothChip;
  };

  const getIconWrapperClass = () => {
    if (size === 'small') return `${classes.iconWrapper} ${classes.iconWrapperSmall}`;
    if (size === 'large') return `${classes.iconWrapper} ${classes.iconWrapperLarge}`;
    return classes.iconWrapper;
  };

  const getIconClass = () => {
    if (size === 'small') return `${classes.boothIcon} ${classes.boothIconSmall}`;
    if (size === 'large') return `${classes.boothIcon} ${classes.boothIconLarge}`;
    return classes.boothIcon;
  };

  const getLabelClass = () => {
    if (size === 'small') return `${classes.label} ${classes.labelSmall}`;
    if (size === 'large') return `${classes.label} ${classes.labelLarge}`;
    return classes.label;
  };

  const getNumberClass = () => {
    if (size === 'small') return `${classes.boothNumber} ${classes.boothNumberSmall}`;
    if (size === 'large') return `${classes.boothNumber} ${classes.boothNumberLarge}`;
    return classes.boothNumber;
  };

  const getPlainTextClass = () => {
    if (size === 'small') return `${classes.plainText} ${classes.plainTextSmall}`;
    if (size === 'large') return `${classes.plainText} ${classes.plainTextLarge}`;
    return classes.plainText;
  };

  const getPlainTextIconWrapperClass = () => {
    if (size === 'small') return `${classes.plainTextIconWrapper} ${classes.plainTextIconWrapperSmall}`;
    if (size === 'large') return `${classes.plainTextIconWrapper} ${classes.plainTextIconWrapperLarge}`;
    return classes.plainTextIconWrapper;
  };

  const getPlainTextIconClass = () => {
    if (size === 'small') return `${classes.plainTextIcon} ${classes.plainTextIconSmall}`;
    if (size === 'large') return `${classes.plainTextIcon} ${classes.plainTextIconLarge}`;
    return classes.plainTextIcon;
  };

  const content = chip ? (
    <Chip
      className={getChipClass()}
      label={
        <Box className={classes.chipContent}>
          <Box className={getIconWrapperClass()}>
            <Icon className={getIconClass()}>tablet_mac</Icon>
          </Box>
          <Typography component='span' className={getLabelClass()}>
            {size === 'small' ? (
              <span className={getNumberClass()}>{formattedBoothId}</span>
            ) : (
              <>
                Booth <span className={getNumberClass()}>{formattedBoothId}</span>
              </>
            )}
          </Typography>
        </Box>
      }
      {...otherProps}
    />
  ) : (
    <Typography variant='body2' className={getPlainTextClass()}>
      <Box className={getPlainTextIconWrapperClass()}>
        <Icon className={getPlainTextIconClass()}>tablet_mac</Icon>
      </Box>
      <span>
        {size === 'small' ? (
          <span className={`${classes.plainTextNumber} ${classes.plainTextNumberSmall}`}>{formattedBoothId}</span>
        ) : (
          <>
            Booth <span className={classes.plainTextNumber}>{formattedBoothId}</span>
          </>
        )}
      </span>
    </Typography>
  );

  if (!linkToDetail) {
    return content;
  }

  return (
    <Tooltip title={tooltipText} arrow placement='top'>
      <Link component={RouterLink} to={`/booths/${boothId}`} underline='none'>
        {content}
      </Link>
    </Tooltip>
  );
}
